import { z } from 'zod';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    AllowanceAddParameters,
    AllowanceProduct,
    GeneralPricingMode,
    ProductChargingMode,
} from '@mews-commander/data-access';
import { getBase64 } from '@mews/framework';
import { useCurrentEnterpriseTaxations } from '@mews-commander/legacy-entities/src/selectors/taxEnvironments';
import { useActiveAccountingCategories } from '../../queries/useActiveAccountingCategories';
import { useCurrentService } from '../../queries/useCurrentService';
import { useDetailsSchema } from './detailsSchema';
import { allowancePricingSchema } from './pricingSchema';
import { useProductCreateFormDefaultValues } from './productFormSchema';
import { allowanceOptionsSchema } from './optionsSchema';
import { allowanceAccountingSchema } from './accountingSchema';

export const useAllowanceFormSchema = () => {
    const detailsSchema = useDetailsSchema();
    return detailsSchema.merge(allowancePricingSchema).merge(allowanceOptionsSchema).merge(allowanceAccountingSchema);
};
export type AllowanceFormValues = z.input<ReturnType<typeof useAllowanceFormSchema>>;

export const useAllowanceCreateFormDefaultValues = (): AllowanceFormValues => {
    const { defaultCurrencyCode } = useCurrentEnterprise();
    const activeAccountingCategories = useActiveAccountingCategories();
    const { names, productCategoryId, ordering, taxRates, consumptionMoment } = useProductCreateFormDefaultValues();
    return {
        names,
        productCategoryId,
        permittedAccountingCategoryIds: activeAccountingCategories.map(({ id }) => id),
        ordering,
        taxRates,
        consumptionMoment,
        options: {
            offerToEmployee: true,
        },
        pricing: {
            creditAmount: 0,
            price: 0,
            currency: defaultCurrencyCode,
            charging: ProductChargingMode.Once,
        },
        creditAccountingCategoryId: activeAccountingCategories[0].id,
        chargeAccountingCategoryId: activeAccountingCategories[0].id,
        profitAccountingCategoryId: activeAccountingCategories[0].id,
    };
};

export const useToAllowanceAddParameters = () => {
    const allowanceSchema = useAllowanceFormSchema();
    const { id } = useCurrentService();
    const { pricing: pricingMode } = useCurrentEnterprise();

    return async (values: AllowanceFormValues) =>
        allowanceSchema
            .transform<AllowanceAddParameters>(async ({ image, options, pricing, taxRates, ...other }) => {
                const profitTaxRateCode = Object.values(taxRates).find(Boolean);

                return {
                    ...other,
                    chargingMode: pricing.charging,
                    creditAmount: pricing.creditAmount,
                    image:
                        image && image[0]
                            ? {
                                  name: image[0].name,
                                  type: image[0].type,
                                  data: await getBase64(image[0]),
                              }
                            : null,
                    options: options ?? {
                        billAsPackage: false,
                        offerToCustomer: false,
                        excludePriceFromOffer: false,
                        offerToEmployee: false,
                        createTask: false,
                        proRataCharging: false,
                        selectedByDefault: false,
                    },
                    ordering: other.ordering ?? undefined,
                    price: {
                        ...(pricingMode === GeneralPricingMode.Net
                            ? { netValue: pricing.price }
                            : { grossValue: pricing.price }),
                        value: pricing.price,
                        currency: pricing.currency,
                        taxValues: [],
                    },
                    profitTaxRateCode,
                    serviceId: id,
                };
            })
            .parseAsync(values);
};

export const useToAllowanceEditFormDefaultValues = () => {
    const { pricing: pricingMode } = useCurrentEnterprise();
    const taxations = useCurrentEnterpriseTaxations();
    const activeAccountingCategories = useActiveAccountingCategories();

    return ({
        allowanceChargeAccountingCategoryId: chargeAccountingCategoryId,
        allowanceCreditAccountingCategoryId,
        price,
        charging,
        allowanceProfitAccountingCategoryId: profitAccountingCategoryId,
        allowance: { creditAmount, currency },
        ...other
    }: AllowanceProduct): AllowanceFormValues => ({
        ...other,
        chargeAccountingCategoryId,
        creditAccountingCategoryId: allowanceCreditAccountingCategoryId ?? '',
        permittedAccountingCategoryIds: activeAccountingCategories.map(({ id }) => id),
        pricing: {
            creditAmount,
            price: pricingMode === GeneralPricingMode.Net ? price.netValue : price.grossValue,
            currency,
            charging,
        },
        profitAccountingCategoryId,
        taxRates: pipe(
            taxations,
            array.reduce({}, (currentTaxRates: AllowanceFormValues['taxRates'], currentTaxation) =>
                pipe(
                    option.Do,
                    option.apS('code', option.fromNullable(currentTaxation.code)),
                    option.apS('taxRateCodes', option.fromNullable(currentTaxation.taxRateCodes)),
                    option.chain((taxation) =>
                        pipe(
                            taxation.taxRateCodes,
                            array.findFirst((taxRateCode) =>
                                pipe(
                                    price.taxRates,
                                    option.fromNullable,
                                    option.map((taxRateCodes) => taxRateCodes.includes(taxRateCode)),
                                    option.getOrElse(() => false)
                                )
                            ),
                            (value) => {
                                currentTaxRates[taxation.code] = option.isSome(value) ? value.value : null;
                                return option.some(currentTaxRates);
                            }
                        )
                    ),
                    option.getOrElse(() => currentTaxRates)
                )
            )
        ),
    });
};
